import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Card,
  CardContent,
  Snackbar,
  Tabs,
  Tab,
  TextareaAutosize,
  Pagination,
  Select
} from "@mui/material";
import axios from "axios";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HistoryIcon from "@mui/icons-material/History";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import PendingIcon from "@mui/icons-material/Pending";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import * as XLSX from "xlsx";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const SalaryExport = () => {
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [costCenterFilter, setCostCenterFilter] = useState("all");
  const [costCenters, setCostCenters] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    month: new Date().toLocaleString('default', { month: 'long' }),
    year: new Date().getFullYear().toString()
  });

  // Company settings
  const [settings, setSettings] = useState(null);
  const [bankName, setBankName] = useState('');

  // Export history
  const [exportHistory, setExportHistory] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);

  // Selected export for details
  const [selectedExport, setSelectedExport] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  // Employee list for modal
  const [employeeListDialogOpen, setEmployeeListDialogOpen] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [employeeListLoading, setEmployeeListLoading] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [employeeListExportInfo, setEmployeeListExportInfo] = useState(null);

  // Pagination & search for Export History
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historySearch, setHistorySearch] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
  const historyItemsPerPage = 10;

  // Months list
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 2; i <= currentYear + 1; i++) {
    years.push(i.toString());
  }

  useEffect(() => {
    fetchSettings();
    fetchPendingApprovals();
    fetchCostCenters();
  }, []);

  // Reactive fetch when page/search/status changes
  useEffect(() => {
    fetchExportHistory();
  }, [historyPage, historySearch, historyStatusFilter]);

  const fetchCostCenters = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API_URL}/api/cost-centers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setCostCenters(res.data.data || []);
      }
    } catch (err) {
      //console.error("Error fetching cost centers:", err);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/salary-export/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        setSettings(response.data.data);
        if (response.data.data.bankTemplate) {
          setBankName(response.data.data.bankTemplate.bank_name);
        }
      }
    } catch (error) {
      //console.error("Error fetching settings:", error);
    }
  };

  const fetchExportHistory = async () => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setHistoryLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: historyPage,
        limit: historyItemsPerPage,
        ...(historySearch && { search: historySearch }),
        ...(historyStatusFilter !== 'all' && { status: historyStatusFilter })
      });

      const response = await axios.get(`${API_URL}/api/salary-export/history?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setExportHistory(response.data.data || []);
        setHistoryTotalPages(response.data.pages || 1);
      }
    } catch (error) {
      //console.error("Error fetching export history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/salary-export/pending-approvals`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPendingApprovals(response.data.data || []);
      }
    } catch (error) {
      //console.error("Error fetching pending approvals:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleExport = async () => {
    if (!formData.month || !formData.year) {
      alert("Please select month and year");
      return;
    }

    if (!settings) {
      alert("Payroll settings not configured. Please set up your bank details first.");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        setSubmitting(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/api/salary-export/export`,
        {
          month: formData.month,
          year: formData.year,
          costCenterId: costCenterFilter
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = `Salary_${formData.month}_${formData.year}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage(`Salary file for ${formData.month} ${formData.year} exported successfully! Waiting for approval.`);

      setTimeout(() => {
        fetchExportHistory();
        fetchPendingApprovals();
      }, 1000);

    } catch (error) {
      //console.error("Error exporting salary:", error);
      let errorMessage = "Failed to export salary file. Please try again.";
      if (error.response && error.response.data) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMessage = json.message || errorMessage;
        } catch {
          // If not JSON, use default
        }
      }
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkAsUploaded = async (exportId) => {
    if (!window.confirm("Mark this payroll as uploaded to the bank portal and notify the Finance Manager?")) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/mark-uploaded/${exportId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccessMessage("Payroll marked as uploaded successfully. Finance Manager notified!");
        fetchPendingApprovals();
        fetchExportHistory();
      }
    } catch (error) {
      //console.error("Error marking payroll as uploaded:", error);
      alert(error.response?.data?.message || "Failed to mark payroll as uploaded.");
    }
  };

  const handleApprove = async (exportId) => {
    if (!window.confirm("Are you sure you want to approve this payroll?")) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/approve/${exportId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccessMessage("Payroll approved successfully!");
        fetchPendingApprovals();
        fetchExportHistory();
        if (detailsDialogOpen) {
          setDetailsDialogOpen(false);
        }
      }
    } catch (error) {
      //console.error("Error approving payroll:", error);
      alert("Failed to approve payroll. Please try again.");
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("Please provide a reason for rejection");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/reject/${selectedExport.id}`,
        { reason: rejectReason },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccessMessage("Payroll rejected successfully!");
        setRejectDialogOpen(false);
        setRejectReason('');
        fetchPendingApprovals();
        fetchExportHistory();
        if (detailsDialogOpen) {
          setDetailsDialogOpen(false);
        }
      }
    } catch (error) {
      //console.error("Error rejecting payroll:", error);
      alert("Failed to reject payroll. Please try again.");
    }
  };

  const handleRegenerate = async (exportId) => {
    if (!window.confirm("This will resubmit the rejected export for approval. Continue?")) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/regenerate/${exportId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccessMessage("Export regenerated and resubmitted for approval successfully!");
        fetchExportHistory();
        fetchPendingApprovals();
        if (detailsDialogOpen) {
          setDetailsDialogOpen(false);
        }
      }
    } catch (error) {
      //console.error("Error regenerating export:", error);
      alert("Failed to regenerate export. Please try again.");
    }
  };

  const handleMarkAsPaid = async (exportId) => {
    if (!window.confirm("Confirm that salaries have been disbursed to all employees?")) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/mark-paid/${exportId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccessMessage("Payroll marked as paid successfully!");
        fetchExportHistory();
        if (detailsDialogOpen) {
          setDetailsDialogOpen(false);
        }
      }
    } catch (error) {
      //console.error("Error marking as paid:", error);
      alert("Failed to mark as paid. Please try again.");
    }
  };

  const handleMarkAsFailed = async (exportId) => {
    const reason = prompt("Please provide a reason for failure:");
    if (reason === null) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/salary-export/mark-failed/${exportId}`,
        { error_details: reason || 'Bank processing failed' },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccessMessage("Payroll marked as failed.");
        fetchExportHistory();
        if (detailsDialogOpen) {
          setDetailsDialogOpen(false);
        }
      }
    } catch (error) {
      //console.error("Error marking as failed:", error);
      alert("Failed to mark as failed. Please try again.");
    }
  };

  const handleViewDetails = (exportRecord) => {
    setSelectedExport(exportRecord);
    setDetailsDialogOpen(true);
  };

  const handleViewEmployeeList = async (exportRecord) => {
    if (!exportRecord || !exportRecord.id) return;
    setEmployeeListExportInfo(exportRecord);
    setEmployeeListDialogOpen(true);
    setEmployeeListLoading(true);
    setEmployeeSearchTerm('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/salary-export/export-employees/${exportRecord.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setEmployeeList(response.data.data || []);
      } else {
        setEmployeeList([]);
      }
    } catch (error) {
      //console.error("Error fetching employee list:", error);
      alert("Failed to fetch employee list for this export.");
      setEmployeeList([]);
    } finally {
      setEmployeeListLoading(false);
    }
  };

  const handleDownloadEmployeesExcel = async () => {
    const exportId = employeeListExportInfo?.id || selectedExport?.id;
    if (!exportId) {
      alert("No export record selected for download");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/salary-export/download/${exportId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = employeeListExportInfo?.file_name || `Salary_${employeeListExportInfo?.month_year || 'Export'}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      //console.error("Error downloading bank format excel file:", error);
      alert("Failed to download bank format salary file. Downloading standard table data...");

      if (!employeeList || employeeList.length === 0) return;
      const exportData = employeeList.map((emp, index) => ({
        'S.No': index + 1,
        'Employee ID': emp.employee_id,
        'Employee Name': emp.name,
        'Bank Name': emp.bank_name,
        'Account Holder Name': emp.account_holder,
        'Account Number': emp.account_number,
        'IFSC Code': emp.ifsc_code,
        'Net Salary (INR)': emp.net_salary
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Employees Salary");
      const monthYear = employeeListExportInfo?.month_year ? employeeListExportInfo.month_year.replace(/\s+/g, '_') : 'Salary';
      XLSX.writeFile(workbook, `Salary_Employees_${monthYear}.xlsx`);
    }
  };

  const getStatusChip = (status) => {
    const statusMap = {
      'Pending Approval': { color: 'warning', label: '⏳ Pending Approval' },
      'Uploaded to Bank': { color: 'info', label: '🏦 Uploaded to Bank' },
      'Approved': { color: 'success', label: '✅ Approved' },
      'Rejected': { color: 'error', label: '❌ Rejected' },
      'Paid': { color: 'success', label: '💰 Paid' },
      'Failed': { color: 'error', label: '❌ Failed' },
      'Draft': { color: 'default', label: '📝 Draft' }
    };
    const config = statusMap[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const userRole = (localStorage.getItem('role') || localStorage.getItem('userRole') || 'hr').toLowerCase();

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom  fontWeight="bold">
        Salary Transfer
      </Typography>

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={() => setSuccessMessage(null)}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {successMessage}
        </Alert>
      )}

      <Paper elevation={3} sx={{ padding: 4, mb: 4 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Generate Salary File
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Month"
              name="month"
              value={formData.month}
              onChange={handleChange}
              fullWidth
              required
            >
              {months.map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Year"
              name="year"
              value={formData.year}
              onChange={handleChange}
              fullWidth
              required
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Cost Center"
              name="costCenter"
              value={costCenterFilter}
              onChange={(e) => setCostCenterFilter(e.target.value)}
              fullWidth
            >
              <MenuItem value="all">All Cost Centers</MenuItem>
              {costCenters.map((cc) => (
                <MenuItem key={cc.id} value={cc.id.toString()}>
                  {cc.name} ({cc.code})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleExport}
              disabled={submitting || !settings}
              startIcon={submitting ? <CircularProgress size={20} /> : <DownloadIcon />}
              sx={{ height: '56px' }}
            >
              {submitting ? "Generating..." : "Export Salary File"}
            </Button>
          </Grid>
        </Grid>

        {settings && (
          <Card sx={{ mt: 3, backgroundColor: '#f5f5f5' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Company Bank Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Bank:</strong> {bankName || 'Not configured'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Account:</strong> {settings.account_number || 'Not configured'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>IFSC:</strong> {settings.ifsc_code || 'Not configured'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Transfer Type:</strong> {
                      settings.transfer_type === 'employer_bank'
                        ? 'Employer Bank (One file)'
                        : 'Employee Bank (Separate files)'
                    }
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {!settings && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            Please configure your payroll settings before exporting salary files.
            <Button size="small" color="warning" href="/payroll-settings" sx={{ ml: 2 }}>
              Go to Settings
            </Button>
          </Alert>
        )}
      </Paper>

      {/* Tabs */}
      <Paper elevation={3} sx={{ padding: 4 }}>
      

        {/* Tab 1: Export History */}
        {tabValue === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" color="primary">
                <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                All Exports
              </Typography>
              <Button variant="outlined" size="small" onClick={() => { setHistoryPage(1); fetchExportHistory(); fetchPendingApprovals(); }} disabled={historyLoading} startIcon={<RefreshIcon />}>
                Refresh
              </Button>
            </Box>

            {/* Search + Status Filter */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search by month/year or file name..."
                value={historySearch}
                onChange={(e) => { setHistorySearch(e.target.value); setHistoryPage(1); }}
                sx={{ minWidth: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
              <Select
                size="small"
                value={historyStatusFilter}
                onChange={(e) => { setHistoryStatusFilter(e.target.value); setHistoryPage(1); }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
                <MenuItem value="Transferred">Transferred</MenuItem>
              </Select>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : exportHistory.length === 0 ? (
              <Alert severity="info">No export history found. Export your first salary file to see it here.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><strong>Month/Year</strong></TableCell>
                      <TableCell><strong>File Name</strong></TableCell>
                      <TableCell><strong>Employees</strong></TableCell>
                      <TableCell><strong>Total Amount</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exportHistory.map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell>{record.month_year}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {record.file_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{record.total_employees || 0}</TableCell>
                        <TableCell>{formatCurrency(record.total_amount)}</TableCell>
                        <TableCell>{getStatusChip(record.status)}</TableCell>
                        <TableCell>
                          <Button variant="text" size="small" onClick={() => handleViewDetails(record)}>
                            View
                          </Button>
                          <Button variant="text" size="small" color="primary" onClick={() => handleViewEmployeeList(record)}>
                            Employees
                          </Button>
                          {record.status === 'Rejected' && userRole === 'hr' && (
                            <Button variant="text" size="small" color="warning" onClick={() => handleRegenerate(record.id)}>
                              <ReplayIcon fontSize="small" /> Regenerate
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination Footer */}
            {!historyLoading && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 3,
                  px: 1,
                  py: 1.5,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  flexWrap: 'wrap',
                  gap: 1
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing page <strong>{historyPage}</strong> of <strong>{historyTotalPages}</strong>
                  {' '}— <strong>{exportHistory.length}</strong> record{exportHistory.length !== 1 ? 's' : ''} on this page
                </Typography>
                <Pagination
                  count={historyTotalPages}
                  page={historyPage}
                  onChange={(_, value) => setHistoryPage(value)}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  disabled={historyTotalPages <= 1}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Tab 2: Pending Approvals (Finance / Finance Manager) */}
        {tabValue === 1 && (userRole === 'finance' || userRole === 'finance_manager' || userRole === 'admin') && (
          <Box>
            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
              <PendingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Pending Approvals ({pendingApprovals.length})
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {pendingApprovals.length === 0 ? (
              <Alert severity="success">✅ No pending approvals. All exports have been reviewed.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><strong>Month/Year</strong></TableCell>
                      <TableCell><strong>File Name</strong></TableCell>
                      <TableCell><strong>Employees</strong></TableCell>
                      <TableCell><strong>Total Amount</strong></TableCell>
                      <TableCell><strong>Exported By</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingApprovals.map((record) => (
                      <TableRow key={record.id} hover sx={{ backgroundColor: '#fff8e1' }}>
                        <TableCell>{record.month_year}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {record.file_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{record.total_employees || 0}</TableCell>
                        <TableCell>{formatCurrency(record.total_amount)}</TableCell>
                        <TableCell>{record.exported_by || '-'}</TableCell>
                        <TableCell>
                          {userRole === 'finance' ? (
                            <Button variant="contained" size="small" color="info" onClick={() => handleMarkAsUploaded(record.id)}>
                              🏦 Mark Uploaded to Bank
                            </Button>
                          ) : (
                            <>
                              <Button variant="contained" size="small" color="success" onClick={() => handleApprove(record.id)}>
                                ✅ Approve
                              </Button>
                              <Button variant="contained" size="small" color="error" sx={{ ml: 1 }} onClick={() => {
                                setSelectedExport(record);
                                setRejectDialogOpen(true);
                              }}>
                                ❌ Reject
                              </Button>
                            </>
                          )}
                          <Button variant="text" size="small" sx={{ ml: 1 }} onClick={() => handleViewDetails(record)}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

       
      </Paper>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Export Details</Typography>
            <IconButton onClick={() => setDetailsDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedExport && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Month/Year</Typography>
                  <Typography variant="body1"><strong>{selectedExport.month_year}</strong></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Box sx={{ mt: 1 }}>{getStatusChip(selectedExport.status)}</Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">File Name</Typography>
                  <Typography variant="body2">{selectedExport.file_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Bank</Typography>
                  <Typography variant="body2">{selectedExport.bankTemplate?.bank_name || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Total Employees</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body1"><strong>{selectedExport.total_employees || 0}</strong></Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewEmployeeList(selectedExport)}
                      sx={{ textTransform: 'none', py: 0.25, px: 1 }}
                    >
                      View Employees List
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Total Amount</Typography>
                  <Typography variant="body1" color="primary">
                    <strong>{formatCurrency(selectedExport.total_amount)}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Exported At</Typography>
                  <Typography variant="body2">{new Date(selectedExport.exported_at).toLocaleString()}</Typography>
                </Grid>
                {selectedExport.approved_at && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Approved At</Typography>
                    <Typography variant="body2">{new Date(selectedExport.approved_at).toLocaleString()}</Typography>
                  </Grid>
                )}
                {selectedExport.payment_confirmed_at && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Payment Confirmed At</Typography>
                    <Typography variant="body2">{new Date(selectedExport.payment_confirmed_at).toLocaleString()}</Typography>
                  </Grid>
                )}
                {selectedExport.error_details && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="error">Error Details</Typography>
                    <Typography variant="body2" color="error">{selectedExport.error_details}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 1 }}>
          {selectedExport && (
            <Box sx={{ display: 'flex', gap: 2, width: '100%', flexWrap: 'wrap' }}>
              {selectedExport.status === 'Pending Approval' && (userRole === 'finance' || userRole === 'admin') && (
                <Button variant="contained" color="info" fullWidth onClick={() => {
                  handleMarkAsUploaded(selectedExport.id);
                  setDetailsDialogOpen(false);
                }}>
                  🏦 Mark Uploaded to Bank
                </Button>
              )}
              {selectedExport.status === 'Uploaded to Bank' && (userRole === 'finance_manager' || userRole === 'admin') && (
                <>
                  <Button variant="contained" color="success" fullWidth onClick={() => handleApprove(selectedExport.id)}>
                    ✅ Approve
                  </Button>
                  <Button variant="contained" color="error" fullWidth onClick={() => {
                    setRejectDialogOpen(true);
                    setDetailsDialogOpen(false);
                  }}>
                    ❌ Reject
                  </Button>
                </>
              )}
              {selectedExport.status === 'Approved' && (userRole === 'finance' || userRole === 'admin') && (
                <>
                  <Button variant="contained" color="success" fullWidth onClick={() => handleMarkAsPaid(selectedExport.id)}>
                    💰 Mark as Paid
                  </Button>
                  <Button variant="outlined" color="error" fullWidth onClick={() => handleMarkAsFailed(selectedExport.id)}>
                    ❌ Mark as Failed
                  </Button>
                </>
              )}
              {selectedExport.status === 'Rejected' && userRole === 'hr' && (
                <Button variant="contained" color="warning" fullWidth onClick={() => handleRegenerate(selectedExport.id)}>
                  🔄 Regenerate & Re-export
                </Button>
              )}
            </Box>
          )}
          <Button onClick={() => setDetailsDialogOpen(false)} variant="outlined" sx={{ mt: 1 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" color="error">Reject Payroll</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
            Please provide a reason for rejecting this payroll:
          </Typography>
          <TextareaAutosize
            minRows={4}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              marginTop: '8px',
              fontFamily: 'inherit'
            }}
            placeholder="Enter rejection reason..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>
            Reject Payroll
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee List Dialog */}
      <Dialog
        open={employeeListDialogOpen}
        onClose={() => setEmployeeListDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              👥 Employee Disbursement Details {employeeListExportInfo?.month_year ? `(${employeeListExportInfo.month_year})` : ''}
            </Typography>
            <IconButton onClick={() => setEmployeeListDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <TextField
              size="small"
              placeholder="Search by ID, name, bank, account..."
              value={employeeSearchTerm}
              onChange={(e) => setEmployeeSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadEmployeesExcel}
              disabled={employeeListLoading || employeeList.length === 0}
            >
              Export Excel
            </Button>
          </Box>

          {employeeListLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : employeeList.length === 0 ? (
            <Alert severity="info">No employee records found for this disbursement.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f0f4f8' }}>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Employee ID</strong></TableCell>
                    <TableCell><strong>Employee Name</strong></TableCell>
                    <TableCell><strong>Bank Name</strong></TableCell>
                    <TableCell><strong>Account Holder</strong></TableCell>
                    <TableCell><strong>Account Number</strong></TableCell>
                    <TableCell><strong>IFSC Code</strong></TableCell>
                    <TableCell align="right"><strong>Net Salary</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employeeList
                    .filter((emp) => {
                      const term = employeeSearchTerm.toLowerCase();
                      return (
                        (emp.employee_id && emp.employee_id.toLowerCase().includes(term)) ||
                        (emp.name && emp.name.toLowerCase().includes(term)) ||
                        (emp.bank_name && emp.bank_name.toLowerCase().includes(term)) ||
                        (emp.account_number && emp.account_number.toLowerCase().includes(term)) ||
                        (emp.ifsc_code && emp.ifsc_code.toLowerCase().includes(term))
                      );
                    })
                    .map((emp, index) => (
                      <TableRow key={emp.employee_id || index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{emp.employee_id}</TableCell>
                        <TableCell><strong>{emp.name}</strong></TableCell>
                        <TableCell>{emp.bank_name}</TableCell>
                        <TableCell>{emp.account_holder}</TableCell>
                        <TableCell>{emp.account_number}</TableCell>
                        <TableCell>{emp.ifsc_code}</TableCell>
                        <TableCell align="right"><strong>{formatCurrency(emp.net_salary)}</strong></TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Typography variant="body2" color="textSecondary">
            Total Employees: <strong>{employeeList.length}</strong> | Total Amount: <strong>{formatCurrency(employeeList.reduce((acc, curr) => acc + (curr.net_salary || 0), 0))}</strong>
          </Typography>
          <Button onClick={() => setEmployeeListDialogOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalaryExport;